<template>
    <v-container class="max-height my-5">
        <v-row>
            <h1 class="text-left">Fuel Refills</h1>
        </v-row>

        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="2" sm="4">
                <company-select v-model="formData['company']" :items="companiesList"
                          density="compact" label="Select Company"
                          @update:model-value="()=>{fetchAssetList();fetchReport()}"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-select v-model="formData['assets']" :items="assetsList" chips clearable
                          density="compact" item-title="label" item-value="id" label="Select Asset(s)" multiple
                          @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-date-input
                    v-model="formData['start_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    hide-actions
                    label="Start Date"
                    prepend-icon=""
                    @click:clear="()=>{formData['start_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-date-input
                    v-model="formData['end_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    hide-actions
                    label="End Date"
                    prepend-icon=""
                    @click:clear="()=>{formData['end_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-select v-model="formData['rows']" :items="[10,20,50,75,100]"
                          density="compact" label="No. of Assets per page"
                          @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="auto" class="d-flex align-center">
                <v-btn :loading="downloading" prepend-icon="mdi-download" size="small" variant="tonal"
                       @click="exportCsv">Export CSV</v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="6">
                <Line :data="fuelLogsChartData" :options="fuelLogsChartOptions" style="width:80%"/>
            </v-col>
            <v-col cols="6">
                <Doughnut :data="chartData" :options="chartOptions" style="height:300px"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Asset</th>
                        <th>Refill Counts</th>
                        <th>Total Fuel Qty</th>
                        <th>Avg. Unit Cost</th>
                        <th>Total Tax</th>
                        <th>Total Cost</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in reportData" v-if="reportData.length" class="text-left">
                        <td>{{ item.asset_name }}</td>
                        <td>{{ item.refill_counts }}</td>
                        <td>{{ item.total_qty }}</td>
                        <td>$ {{ parseFloat(item.avg_unit_cost).toFixed(2) }}</td>
                        <td>$ {{ item.total_tax }}</td>
                        <td>$ {{ item.total_cost }}</td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" class="text-center" colspan="6">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="6">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchReport"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>

        <v-row v-if="flatFills.length" class="mt-4">
            <v-col cols="12">
                <div class="d-flex align-center gap-3 mb-3">
                    <span class="text-subtitle-1 font-weight-medium">Individual Fill Log</span>
                    <v-chip v-if="suspiciousFills.length" color="warning" prepend-icon="mdi-alert" size="small">
                        {{ suspiciousFills.length }} suspicious fill{{ suspiciousFills.length !== 1 ? 's' : '' }}
                    </v-chip>
                    <v-chip v-else color="success" prepend-icon="mdi-check-circle" size="small">No anomalies</v-chip>
                    <v-chip
                        v-if="suspiciousFills.length"
                        :color="showSuspiciousOnly ? 'warning' : 'default'"
                        :variant="showSuspiciousOnly ? 'tonal' : 'outlined'"
                        class="ml-auto"
                        prepend-icon="mdi-filter"
                        size="small"
                        @click="showSuspiciousOnly = !showSuspiciousOnly"
                    >{{ showSuspiciousOnly ? 'Showing suspicious only' : 'All fills' }}</v-chip>
                </div>

                <v-table density="compact">
                    <thead>
                    <tr>
                        <th>Asset</th>
                        <th>Date</th>
                        <th>Distance Since Last Fill</th>
                        <th>Flag</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="fill in displayedFills" :key="`${fill.asset_id}-${fill.created_at}-${fill.idx}`">
                        <td>{{ fill.asset_name }}</td>
                        <td>{{ fill.created_at }}</td>
                        <td>{{ fill.distance != null ? fill.distance + ' km' : '—' }}</td>
                        <td>
                            <v-tooltip v-if="fill.flags.length" location="start">
                                <template v-slot:activator="{ props }">
                                    <v-chip v-bind="props" color="warning" prepend-icon="mdi-alert" size="x-small">
                                        {{ fill.flags.length }} flag{{ fill.flags.length !== 1 ? 's' : '' }}
                                    </v-chip>
                                </template>
                                <div v-for="flag in fill.flags" :key="flag">• {{ flag }}</div>
                            </v-tooltip>
                            <v-icon v-else color="success" size="small">mdi-check-circle-outline</v-icon>
                        </td>
                    </tr>
                    <tr v-if="!displayedFills.length">
                        <td class="text-center text-medium-emphasis" colspan="4">No fills match current filter</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {Chart, registerables} from 'chart.js'
import {useAuthStore} from "@/stores/authStore";
import {VDateInput} from "vuetify/lib/labs/VDateInput";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useReportExport} from "@/composables/useReportExport";
import {Doughnut, Line} from "vue-chartjs";
import CompanySelect from "@/components/globals/company-select.vue";

Chart.register(...registerables);

const {user} = useAuthStore();
const {downloadReport} = useReportExport();
const fuelLogs = ref([]);
const reportData = ref([]);
const loading = ref(false);
const downloading = ref(false);
const totalPages = ref(1);
const currentPage = ref(1);
const assetsList = ref([]);
const companiesList = ref([]);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    company: ref(user.type !== 'admin' ? user.company.id : null),
    start_date: null,
    end_date: null,
    assets: null,
    rows: 10,
});

const fuelLogsChartOptions = {
    responsive: true,
    plugins: {
        title: {
            display: true,
            text: 'Distance Between Fuel Fill-ups'
        },
        legend: {
            display: false,
        }
    },
    scales: {
        y: {
            title: {
                display: true,
                text: 'Distance (KM)'
            }
        },
        x: {
            title: {
                display: true,
                text: 'Date'
            }
        }
    },
};

const fuelLogsChartData = computed(() => {
    if (!Object.keys(fuelLogs.value).length) {
        return {
            labels: [],
            datasets: []
        };
    }

    // Collect all unique dates across all assets
    const dateSet = new Set()
    Object.values(fuelLogs.value).forEach(logs => {
        logs.forEach(log => dateSet.add(log.created_at))
    })

    // labels
    const labels = Array.from(dateSet).sort();
    // Build datasets
    const datasets = Object.entries(fuelLogs.value).map(([assetName, logs], index) => {
        const dataMap = Object.fromEntries(logs.map(log => [log.created_at, log.distance ?? null]))

        return {
            label: assetName,
            data: labels.map(date => dataMap[date] ?? null),
            borderColor: `hsl(${Math.random() * 360}, 70%, 60%)`,
            fill: false,
            tension: 0.3,
        }
    });

    return {
        labels,
        datasets: datasets
    }
})

const chartOptions = {
    cutout: '45%', // default ~50%, increase for a thinner ring
    responsive: true,
    maintainAspectRatio: false, // allow resizing
    plugins: {
        legend: {
            position: 'right',
            labels: {
                boxWidth: 16,
                padding: 10
            }
        },
        tooltip: {
            callbacks: {
                label: (ctx) => ` $${ctx.parsed.toFixed(2)}`
            }
        }
    }
};

const chartData = computed(() => {
    if (!reportData.value.length) {
        return {
            labels: [],
            datasets: [{
                data: [],
                backgroundColor: reportData.value.map(() => `hsl(${Math.random() * 360}, 70%, 60%)`),
            }]
        };
    }
    const labels = reportData.value.map(item => item.asset_name)
    const values = reportData.value.map(item => item.total_cost)

    return {
        labels,

        datasets: [{
            data: values,
            backgroundColor: reportData.value.map(() => `hsl(${Math.random() * 360}, 70%, 60%)`),
        }]
    }
})

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        fetchReport();
        fetchAssetList();
    }
})

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companiesList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchAssetList = () => {
    return send('get', `assets/list`, {company: formData['company']})
        .then((response) => {
            assetsList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Assets List could not be loaded', 'error')
        })
}
const exportCsv = async () => {
    downloading.value = true
    try {
        await downloadReport('fleet/reports/assets-fuel-report', formData, 'csv', 'fuel-refill.csv')
    } catch {
        showSnackbar('Export failed', 'error')
    } finally {
        downloading.value = false
    }
}

const showSuspiciousOnly = ref(false)

const flatFills = computed(() => {
    const fills = []
    for (const [assetName, logs] of Object.entries(fuelLogs.value)) {
        logs.forEach((log, idx) => fills.push({ ...log, asset_name: assetName, idx }))
    }
    return fills
})

const flaggedFills = computed(() => flatFills.value.map(fill => {
    const flags = []
    if (fill.distance === 0) flags.push('No odometer advance since last fill')
    if (fill.distance != null && fill.distance < 0) flags.push('Odometer reversal — possible tampering')
    return { ...fill, flags }
}))

const suspiciousFills = computed(() => flaggedFills.value.filter(f => f.flags.length > 0))

const displayedFills = computed(() =>
    showSuspiciousOnly.value ? suspiciousFills.value : flaggedFills.value
)

const fetchReport = () => {
    loading.value = true;
    showSuspiciousOnly.value = false;
    send('get', `fleet/reports/assets-fuel-report`, {...formData, page: currentPage.value})
        .then((response) => {
            fuelLogs.value = response.data.data.fuelLogs;
            reportData.value = response.data.data.reportData.data;
            totalPages.value = response.data.data.reportData.last_page;
            currentPage.value = response.data.data.reportData.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Fuel Refill report could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
</script>
